import styled, { createGlobalStyle } from 'styled-components';
import { tokens } from '@splashthat/doppler';
import { mediaMaxWidth } from 'common/styles/breakpoints';
import {
    HIGHLIGHT_AND_CONTENT_EDITING_BEHAVIOR_CSS,
    BORDER_PROPERTIES,
    FONT_PROPERTIES,
    BACKGROUND_PROPERTIES,
} from 'common/components/TouchpointEditor/components/TouchpointContainer';
import DropdownMenu from '@radix-ui/react-dropdown-menu';

/*
 * This is a global style that is applied to all instances of the SuiMenuItem component within the SessionFilterDropdown
 * component. Has been done because of a restriction on SuiDropdown select which don't allow us to modify its styles.
 * Last but not least should be a temporary approach to fulfill design needs until we create the new version of SUI
 * components with better API and styling.
 */
export const SessionFilterDropdownCustomStyles = createGlobalStyle`
    .LayerPortal .SessionFilterDropdown {
        // Color applied on the background of the popover
        background-color: ${(props) => props.$backgroundColor} !important;
        border-color: ${(props) => props.$backgroundColor} !important;

        color: ${(props) => props.$fontColor} !important;
        font-family: '${(props) => props.$fontFamily}', sans-serif !important;

        // Color applied to each row on hover:
        .SuiContainer.SuiMenuItem.is-focused, .SuiContainer.SuiMenuItem.is-hovered {
            background-color: ${(props) => props.$backgroundColorHover} !important;
        }

        .SuiCheckbox__checkbox {
            .SuiCheckbox__indicator {
                background-color: transparent;
                &::before {
                    background-color: transparent !important;
                }

                .SuiCheckbox__icon {
                    color: ${(props) => props.$iconColor};
                }
            }
            &::before {
                // Border applied when the checkbox is checked or clicked
                border-color: ${(props) => props.$iconColor} !important;
            }
            // Border applied when the checkbox is unchecked
            border-color: ${(props) => props.$iconColor} !important;
        }
    }
`;

export const StyledDropdownMenuTrigger = styled(DropdownMenu.Trigger)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    ${BACKGROUND_PROPERTIES}
    ${BORDER_PROPERTIES}
    ${FONT_PROPERTIES}
    width: 13.5rem;
    ${mediaMaxWidth.medium`
        width: 100%
    `}

    height: 3rem;
    outline: none;
    border: ${(props) => props.$borderWidth}px solid;
    border-color: ${(props) => props.$borderColor};
    & > svg {
        color: ${(props) => props.$iconColor};
    }
    &:hover {
        background-color: ${(props) => props.$backgroundColorHover};
        border-color: ${(props) => props.$borderColorHover};
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.$borderColor};
        outline-offset: 2px;
    }
`;

export const StyledDropDownMenuSubTrigger = styled(DropdownMenu.SubTrigger)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: ${(props) => props.$backgroundColor};
    padding: 0.375rem 0.75rem;
    width: 13.5rem;
    ${mediaMaxWidth.medium`width: 100%`}

    height: 3rem;
    outline: none;
    cursor: pointer;
    ${FONT_PROPERTIES}
    & > svg {
        color: ${(props) => props.$iconColor};
    }
    &:hover {
        outline: none;
        background-color: ${(props) => props.$backgroundColorHover};
        border-width: 0;
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.$borderColor};
        outline-offset: 2px;
    }

    &:hover:focus-visible {
        outline: none;
    }
`;

export const StyledDropdownMenuContent = styled(DropdownMenu.Content)`
    box-shadow: ${tokens.objectStyles.shadow};
`;

export const StyledDropdownSubContent = styled(DropdownMenu.SubContent)`
    background-color: ${(props) => props.$backgroundColor};
    padding: 0;
    width: 15rem;
    overflow-y: scroll;
    outline: none;
    max-height: 15rem;
    box-shadow: ${tokens.objectStyles.shadow};
    ${FONT_PROPERTIES}
    ${mediaMaxWidth.medium`width: 100%`}
`;

export const SuiDropdownSelectWrapper = styled.div`
    .SuiPopoverMenu {
        height: 100%;
    }

    .SuiField {
        border-style: solid;
        background-color: ${(props) => props.$backgroundColor};
        font-family: '${(props) => props.$fontFamily}', sans-serif;
        height: 100%;

        ${BORDER_PROPERTIES}

        &:hover {
            background-color: ${(props) => props.$backgroundColorHover};
            border-color: ${(props) => props.$borderColorHover};
        }

        > div {
            height: 100%;
        }

        .SuiField__current,
        .SuiField__placeholder {
            color: ${(props) => props.$fontColor};
            font-size: ${(props) => props.$fontSize}px;

            &:hover {
                color: ${(props) => props.$fontColorHover};
            }
        }

        .SuiField__icon-right {
            color: ${(props) => props.$iconColor};
        }

        ${HIGHLIGHT_AND_CONTENT_EDITING_BEHAVIOR_CSS}
    }
`;

export const CheckboxSubItem = styled(DropdownMenu.CheckboxItem)`
    all: unset;
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem;
    background-color: ${(props) => props.$backgroundColor || 'transparent'};
    cursor: pointer;

    &:hover,
    &:hover[data-highlighted] {
        background-color: ${(props) => props.$backgroundColorHover};
        outline: none;
    }

    &:focus-visible,
    &[data-highlighted] {
        outline: 2px solid ${(props) => props.$borderColor};
        outline-offset: -2px;
    }
`;

export const CheckboxFrame = styled.span`
    display: flex;
    min-width: 18px;
    width: 18px;
    height: 18px;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    border-radius: 4px;
    border: 1px solid;
    color: ${(props) => props.$iconColor};
    cursor: pointer;
`;

export const CheckboxIndicator = styled(DropdownMenu.ItemIndicator)`
    display: flex;
    & > svg {
        color: ${(props) => props.iconColor};
    }
`;

export const TextWithOverflow = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const Filterlabel = styled.span`
    display: flex;
    align-items: center;
`;

export const FilterIndicator = styled.span`
    background-color: ${(props) => props.$backgroundColor};
    color: ${(props) => props.$fontColor};
    margin-left: 4px;
    border-radius: 10px;
    font-size: 0.75rem;
    padding: 2px 5px;
    min-width: 18px;
`;
